import { css, StyleSheet } from 'aphrodite'
import * as React from 'react'

import {
  isThemeBossLevel,
  THEMES,
  themeForLevel,
} from '../../content/levels'
import { IUnitType } from '../../engine/unit'
import Dialog from '../components/Dialog'
import Screen from '../components/screen'
import UnitGlyph from '../components/unitGlyph'
import MainStore from '../mainStore'
import style from '../utils/style'
import HelpDialog from './helpDialog'
import ShopDialog from './shopDialog'

const styles = StyleSheet.create({
  main: {
    textAlign: 'center',
    border: style.border,
    overflow: 'auto',
  },
  button: {
    cursor: 'pointer',
    ':hover': {
      color: 'white',
    },
  },
  blockedLevel: {
    opacity: .6,
  },
  unit: {
    padding: 10,
    width: 50,
    height: 50,
    fill: style.textColor,
    stroke: 'black',
  },
  partyUnits: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    maxWidth: 700,
    margin: '0 auto',
  },
  adminPanel: {
    maxWidth: 620,
    margin: '18px auto',
    padding: 12,
    border: `3px solid ${style.gold}`,
    background: 'rgba(133, 113, 0, .08)',
  },
  adminControls: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
  },
  adminControl: {
    margin: 6,
  },
  adminInput: {
    width: 72,
    marginLeft: 6,
    background: style.darkGrey,
    color: style.textColor,
    border: `2px solid ${style.gold}`,
    fontFamily: 'VT323, monospace',
    fontSize: 20,
  },
  adminSelect: {
    marginLeft: 6,
    background: style.darkGrey,
    color: style.textColor,
    border: `2px solid ${style.gold}`,
    fontFamily: 'VT323, monospace',
    fontSize: 20,
  },
})

interface IProps {
  store: MainStore,
}

interface IState {
  showShop: boolean
  showHelp: boolean
  showResetConfirm: boolean
  adminRound: number
  adminTheme: string
}

export default class MainView extends React.Component<IProps, IState> {
  state = {
    showShop: false,
    showHelp: false,
    showResetConfirm: false,
    adminRound: 1,
    adminTheme: THEMES[0].id,
  }

  onStartLevel = () => {
    const { store } = this.props
    store.startGame(store.state.levelReached)
  }

  renderLevelButton = (_, levelNumber: number) => {
    // TODO this should be geq?
    const { store } = this.props
    const reached = store.state.levelReached === levelNumber
    return (
      <h3
        className={css(reached ? styles.button : styles.blockedLevel)}
        onClick={reached ? () => store.startGame(levelNumber) : undefined}
        key={levelNumber}
      >
        Level {levelNumber + 1}
      </h3>
    )
  }

  renderPartyUnit(unit: IUnitType, idx: number) {
    return <UnitGlyph unitType={unit} key={idx} wrapped={true} />
  }

  renderShop() {
    if (!this.state.showShop) {
      return null
    }

    return (
      <ShopDialog
        onCancel={() => this.setState({ showShop: false })}
        store={this.props.store}
      />
    )
  }

  renderHelp() {
    if (!this.state.showHelp) {
      return null
    }

    return (
      <HelpDialog onCancel={() => this.setState({ showHelp: false })} />
    )
  }

  renderResetConfirm() {
    if (!this.state.showResetConfirm) {
      return null
    }

    const { store } = this.props
    return (
      <Dialog compact>
        <Dialog.Title>Reset Progress?</Dialog.Title>
        <Dialog.Content>
          This will clear your party, money, and depth reached.
        </Dialog.Content>
        <Dialog.Controls>
          <Dialog.Control onClick={() => {
            store.resetProgress()
            this.setState({ showResetConfirm: false })
          }}>
            Reset Progress
          </Dialog.Control>
          <Dialog.Control onClick={() =>
            this.setState({ showResetConfirm: false })
          }>
            Cancel
          </Dialog.Control>
        </Dialog.Controls>
      </Dialog>
    )
  }

  onAdminRoundChange = (e: any) => {
    const adminRound = Math.max(1, parseInt(e.target.value, 10) || 1)
    this.setState({ adminRound })
  }

  onAdminThemeChange = (e: any) => {
    this.setState({ adminTheme: e.target.value })
  }

  renderAdmin() {
    const { store } = this.props
    if (!store.state.adminUnlocked) {
      return null
    }

    const level = this.state.adminRound - 1
    const roundTheme = themeForLevel(level)
    const selectedTheme = themeForLevel(level, this.state.adminTheme)
    const bossText = isThemeBossLevel(level) ? 'Boss round' : 'Standard round'

    return (
      <div className={css(styles.adminPanel)}>
        <h2>Admin</h2>
        <div className={css(styles.adminControls)}>
          <label className={css(styles.adminControl)}>
            Round
            <input
              className={css(styles.adminInput)}
              type="number"
              min="1"
              value={this.state.adminRound}
              onChange={this.onAdminRoundChange}
            />
          </label>
          <label className={css(styles.adminControl)}>
            Theme
            <select
              className={css(styles.adminSelect)}
              value={this.state.adminTheme}
              onChange={this.onAdminThemeChange}
            >
              {THEMES.map(t => (
                <option value={t.id} key={t.id}>{t.name}</option>
              ))}
            </select>
          </label>
        </div>
        <div>Round {this.state.adminRound}: {roundTheme.name} - {bossText}</div>
        <div>Random map theme: {selectedTheme.name}</div>
        <h3
          className={css(styles.button)}
          onClick={() => store.startGame(level)}
        >
          Launch Test Round
        </h3>
        <h3
          className={css(styles.button)}
          onClick={() => store.startGeneratedMap(level, selectedTheme.id)}
        >
          Generate Random {selectedTheme.name} Map
        </h3>
      </div>
    )
  }

  render() {
    const { store } = this.props
    const shop = this.renderShop()
    const help = this.renderHelp()
    const resetConfirm = this.renderResetConfirm()
    const admin = this.renderAdmin()
    const { levelReached } = store.state
    const nextTheme = themeForLevel(levelReached)
    const nextRoundType = isThemeBossLevel(levelReached)
      ? 'Boss round'
      : 'Standard round'

    return (
      <Screen classes={[styles.main]}>
        {shop}
        {help}
        {resetConfirm}
        <h1>Hexa Battle</h1>
        <h2 className={css(styles.button)} onClick={this.onStartLevel}>
          {levelReached ? 'Venture Deeper' : 'Start your adventure'}
        </h2>
        {!!levelReached && <h3>Depth reached: {levelReached}</h3>}
        <h3>Next: {nextTheme.name} - {nextRoundType}</h3>
        <div className={css(styles.partyUnits)}>
          {store.state.party.map(this.renderPartyUnit)}
        </div>
        {admin}
        <h2
          onClick={() => this.setState({ showShop: true })}
          className={css(styles.button)}
        >
          Shop
        </h2>
        <h2
          onClick={() => this.setState({ showHelp: true })}
          className={css(styles.button)}
        >
          Help
        </h2>
        <h2
          onClick={() => this.setState({ showResetConfirm: true })}
          className={css(styles.button)}
        >
          Reset Progress
        </h2>
      </Screen>
    )
  }
}



// WEBPACK FOOTER //
// ./src/ui/mainView/index.tsx